import {
  appointmentDeviation,
  appointmentSlot,
  calendarDay,
  counterVisitLabel,
  inquiryPurposeLabel,
  townOf,
} from '@ghp/domain'
import {
  Badge,
  Button,
  Card,
  DataTable,
  DataTableRowAction,
  EmptyState,
  FilterBar,
  InfoNote,
  Page,
  PageBody,
  PageHeader,
  PageToolbar,
  SearchField,
  SegmentedControl,
  Select,
  Tag,
  UrgentStack,
} from '@ghp/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createRoute,
  stripSearchParams,
  useNavigate,
  type SearchSchemaInput,
} from '@tanstack/react-router'
import {
  CalendarClock,
  CheckCheck,
  ChevronRight,
  ClipboardList,
  Paperclip,
  Plus,
  RotateCcw,
  Send,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { shellRoute } from '../../../app/routes/root'
import { useTeamData } from '../../../app/use-team-data'
import type { CachedInquiry, InspectionDraft, OutboxState } from '../../../offline/data-db'
import { createFreeInspection } from '../../../offline/free-inspection'
import { latestSubmissions } from '../../../offline/outbox'
import { discardOutOfTour, type DiscardResult } from '../../../offline/discard-out-of-tour'
import { pullInquiries } from '../../../offline/pull'
import {
  DRAFTS_QUERY_KEY,
  FIELD_APPOINTMENTS_QUERY_KEY,
  INQUIRIES_QUERY_KEY,
  MEDIA_QUERY_KEY,
  OUTBOX_QUERY_KEY,
  PASSAGE_NOTICES_QUERY_KEY,
} from '../../../offline/query-keys'
import {
  SYNC_PRESENTATION,
  TOUR_STATE_PRESENTATION,
  pullPresentationOf,
  type PullPresentation,
} from '../labels'
import {
  LIST_SCOPE_DEFAULTS,
  NO_FILTERS,
  applyFilters,
  citiesOf,
  clientsOf,
  lotLabel,
  lotsOf,
  purposesOf,
  hasActiveFilters,
  partitionScopes,
  statesOf,
  tourStateOf,
  validateListScopeSearch,
  type ListFilters,
  type ListScope,
  type ListScopeSearch,
  type ListSort,
  type TourState,
  hasQueuedHandOver,
} from '../lib/list-filters'
import { usePassageNotice } from '../use-passage-notice'
import { useFieldAppointment } from '../use-field-appointment'
import { useAppointmentMove } from '../use-appointment-move'
import { useTourOccupancy } from '../use-tour-occupancy'
import { FreeInspectionDialog } from '../components/FreeInspectionDialog'
import { OpeningSheet } from '../components/OpeningSheet'
import { OrphanDrafts } from '../components/OrphanDrafts'
import { useLockedInquiryIds } from '../lib/locked-drafts'
import { usePassageNoticedAt } from '../lib/passage-notices'
import { orphanDraftsOf } from '../lib/orphan-drafts'
import s from './my-inspections.module.css'

/** « Mes contrôles » — la tournée de l'équipe connectée, lue **en cache local** (D21). */
export const myInspectionsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/',
  // ⚠️ `SearchSchemaInput` sépare ce qu'on **accepte** en entrée de ce qu'on **rend** : sans lui,
  // TanStack déduit l'entrée du retour et exige `search` sur chaque `<Link to>` de l'app, jusque
  // sur la redirection de connexion. Une vue absente vaut « À faire ».
  validateSearch: (search: Record<string, unknown> & SearchSchemaInput): ListScopeSearch =>
    validateListScopeSearch(search),
  search: {
    // « À faire » ne s'écrit pas dans l'URL : `/` reste `/`, seule une vue choisie s'y voit.
    middlewares: [stripSearchParams<ListScopeSearch>(LIST_SCOPE_DEFAULTS)],
  },
  component: MyInspections,
})

/** Une ligne de tournée : la demande, plus ce que seule la tablette sait à son sujet. */
interface InspectionRow {
  inquiry: CachedInquiry
  /** `null` tant que rien n'a été transmis pour ce contrôle. */
  sync: OutboxState | null
  /** `null` tant que le serveur n'a pas accepté l'envoi — cf. `partitionScopes`. */
  sentAt: string | null
  /** Le dernier avis de passage laissé depuis la tablette, `null` sans avis (Terrain 2.8). */
  passageNoticedAt: string | null
  attachments: number
  /**
   * Le jour où l'agent dit être passé, lu dans le brouillon local. `null` tant qu'aucune fiche
   * n'est ouverte — la colonne « RDV » n'affiche alors que le rendez-vous, sans écart possible.
   */
  performedOn: string | null
}

/** Les tris offerts, dans les mots et l'ordre du prototype. */
const SORT_OPTIONS: readonly { value: ListSort; label: string }[] = [
  { value: 'street', label: 'Rue (A→Z)' },
  { value: 'city', label: 'Ville' },
  { value: 'reference', label: 'Réf. GHP' },
  { value: 'state', label: 'État' },
]

/**
 * Où en est la descente de la tournée, **du point de vue de l'agent**.
 *
 * Une liste vide a des causes qui ne se ressemblent pas, et devant chacune l'agent ne fait pas
 * la même chose : attendre le réseau, refaire sa connexion, appeler le bureau, ou ne rien faire
 * du tout parce qu'on ne lui a rien affecté. Un message unique — « elle descendra dès que la
 * tablette retrouvera du réseau » — les couvre toutes à tort : il fait attendre un retour de
 * réseau qui ne changera rien à un refus du serveur, et laisse croire à une panne là où la
 * tournée est simplement vide.
 *
 * Le classement du motif vit dans `labels.ts` (`pullPresentationOf`) : le référentiel échoue
 * pour les mêmes raisons que la tournée, et l'éditeur s'en sert aussi.
 */
type TourDownload = 'running' | PullPresentation

/**
 * Ce que l'écran dit d'une tournée vide, selon ce qui s'est passé. Les mots portent la
 * conduite à tenir : sans elle, l'agent reste devant un écran vide sans savoir s'il doit
 * attendre, se reconnecter ou téléphoner.
 */
const EMPTY_TOUR: Record<TourDownload, { title: string; body: string }> = {
  running: {
    title: 'Téléchargement de la tournée…',
    body: 'Les contrôles affectés à votre équipe sont en cours de réception.',
  },
  done: {
    title: 'Aucun contrôle affecté',
    body: "Aucun contrôle n'est affecté à votre équipe pour le moment. Ils apparaîtront ici dès que le bureau vous en aura confié.",
  },
  // ⚠️ Le refus couvre deux causes que le serveur ne distingue pas d'ici : un compte sans
  // rôle terrain, et une session que le serveur ne reconnaît plus. Les mots doivent donc
  // valoir pour les deux — surtout ne pas laisser entendre que le compte est le mauvais,
  // ce qui ferait quitter la session à un agent en pleine tournée pour une simple péremption.
  refused: {
    title: 'Tournée inaccessible',
    body: "Le serveur a refusé l'accès aux contrôles avec ce compte. Attendre le réseau n'y changera rien : reconnectez-vous avec un compte d'agent de terrain, et prévenez le bureau si le refus persiste.",
  },
  unreachable: {
    title: 'Aucun contrôle',
    body: "La tournée n'a pas encore été téléchargée. Elle descendra dès que la tablette retrouvera du réseau.",
  },
  faulty: {
    title: 'Tournée indisponible',
    body: "La tournée n'a pas pu être téléchargée, et ce n'est pas une affaire de réseau : prévenez le bureau, elle ne descendra pas d'elle-même.",
  },
}

/**
 * Ce que chaque vue dit quand elle est vide, la tournée ne l'étant pas. Distinct d'`EMPTY_TOUR` :
 * là l'agent n'a rien reçu, ici il a tout fait — ou n'a encore rien transmis.
 */
const EMPTY_SCOPE: Record<ListScope, { title: string; body: string }> = {
  todo: {
    title: 'Tournée terminée',
    body: 'Tous les contrôles de la tournée sont partis au bureau. Ils restent consultables dans « Transmis » — et si le bureau en renvoie un pour correction, il reviendra ici.',
  },
  transmitted: {
    title: 'Aucun contrôle transmis',
    body: "Les contrôles dont le serveur a accusé réception viendront ici. Tant qu'un envoi est en attente, en échec ou refusé, le contrôle reste dans « À faire ».",
  },
}

function MyInspections() {
  const { shared, sync, accessToken, auth, requestSync, planning } =
    myInspectionsRoute.useRouteContext()
  const data = useTeamData()
  const queryClient = useQueryClient()
  const recordNotice = usePassageNotice(auth, requestSync)
  const recordAppointmentOnSite = useFieldAppointment(auth, requestSync)
  const appointmentMove = useAppointmentMove({ auth, planning, accessToken, requestSync })
  const occupancy = useTourOccupancy()
  const [filters, setFilters] = useState<ListFilters>(NO_FILTERS)
  const [freeInspection, setFreeInspection] = useState(false)
  const { view: scope } = myInspectionsRoute.useSearch()
  const navigate = useNavigate({ from: myInspectionsRoute.fullPath })
  const [download, setDownload] = useState<TourDownload>('running')
  /**
   * Le contrôle dont la pré-fiche est ouverte, `null` sinon — la **ligne** et non la seule
   * demande : c'est elle qui porte l'état de tournée, donc ce que la fenêtre propose de faire.
   */
  const [opening, setOpening] = useState<InspectionRow | null>(null)
  const locked = useLockedInquiryIds()

  const inquiries = useQuery({
    queryKey: INQUIRIES_QUERY_KEY,
    queryFn: () => data.inquiries.toArray(),
  })
  const outbox = useQuery({
    queryKey: OUTBOX_QUERY_KEY,
    queryFn: () => data.outbox.toArray(),
  })
  /**
   * Les brouillons, lus **pour eux-mêmes** et non par la tournée (#147) : une saisie dont la
   * demande a été réaffectée n'a plus de ligne dans la liste, et c'est la seule lecture qui la
   * ramène à l'écran. L'observateur `liveQuery` invalide cette clé — la section suit donc les
   * écritures, y compris celles d'un rejeu d'arrière-plan.
   *
   * C'est aussi là que vit le jour du passage, qu'oppose au rendez-vous la colonne « RDV » —
   * la seule chose qui rende un contrôle décalé visible depuis la tournée (#115).
   */
  const noticedAt = usePassageNoticedAt()
  const drafts = useQuery({
    queryKey: DRAFTS_QUERY_KEY,
    queryFn: () => data.drafts.toArray(),
  })
  const attachments = useQuery({
    queryKey: MEDIA_QUERY_KEY,
    // ⚠️ Les **clés de l'index**, pas les enregistrements : un `inquiryId` par pièce. Lire les
    // médias entiers chargerait en mémoire toutes les photos et vidéos en attente d'envoi pour
    // n'en compter que le nombre.
    queryFn: async () => countByInquiry((await data.media.orderBy('inquiryId').keys()) as string[]),
  })

  /**
   * La tournée redescend à l'ouverture de l'écran (D22 pt.2) — **pas au moment où l'agent
   * ouvre une fiche**. Hors réseau, `pullInquiries` rend `unavailable` : le cache de la
   * veille reste affiché, rien ne s'efface, aucune erreur ne remonte à l'écran.
   *
   * Le **motif** du résultat est retenu : c'est la seule chose qui distingue une tournée qui
   * n'est pas encore descendue d'une tournée vide ou d'un accès refusé, et l'état vide n'a
   * rien d'autre pour ne pas mentir à l'agent.
   *
   * ⚠️ **La base est une dépendance, et c'est la seule qui varie.** Les quatre autres sont
   * stables sur la vie de l'écran (`accessToken` est le getter du contexte de route, pas une
   * valeur) : sans elle, une tablette qui change de main — le scénario même que le cloisonnement
   * adresse, et qui **navigue** au lieu de recharger la page — lirait bien la base de l'équipe
   * entrante, vide, mais ne **demanderait** jamais sa tournée. Rien ne descendrait avant un tour
   * de planificateur *dû* : soixante secondes de tick, mais dix minutes de fraîcheur.
   *
   * ⚠️ C'est aussi pourquoi la dépendance est la base et non la session : celle-ci change à
   * chaque refresh de jeton (M23), et redescendre la tournée tous les quarts d'heure n'est pas
   * le débit d'un chantier. Une base ne change que quand la tablette change de main.
   */
  useEffect(() => {
    let showing = true

    // Une nouvelle passe repart de « running » : sans cette remise à zéro, un état terminal
    // de la passe précédente (« reconnectez-vous ») resterait affiché pendant que la
    // tentative en vol s'apprête, peut-être, à le contredire.
    setDownload('running')

    // Le `401` remonte à la session comme partout ailleurs : ouvrir l'écran avec un jeton mort
    // doit déclencher la redescente en dégradé, pas attendre le tour de planificateur suivant.
    pullInquiries(data, shared, sync, accessToken(), (token) => auth.tokenRefused(token)).then(
      (result) => {
        // L'invalidation suit l'écriture, pas l'écran : même si l'agent est déjà parti,
        // `bulkPut` a rangé des demandes (`updated` comme `partial`), et les sauter
        // laisserait le cache pré-descente s'afficher au retour, avec `staleTime: Infinity`,
        // jusqu'au prochain tour du planificateur. La clé des brouillons part avec : une
        // demande qui vient d'arriver doit rouvrir un éditeur resté sur « Contrôle inconnu »
        // (`openDraft` avait mis `null` en cache).
        if (result.outcome !== 'unavailable') {
          void queryClient.invalidateQueries({ queryKey: INQUIRIES_QUERY_KEY })
          void queryClient.invalidateQueries({ queryKey: DRAFTS_QUERY_KEY })
        }

        if (showing) setDownload(pullPresentationOf(result))
      },
      (failure: unknown) => {
        // Une descente ne rejette pas pour un refus ni pour une coupure — celles-là arrivent
        // en résultat. Ce qui passe ici est une écriture locale en échec : le dire, sinon
        // « Téléchargement… » resterait à l'écran pour toujours.
        console.error('[terrain] Descente de la tournée impossible', failure)
        if (showing) setDownload('faulty')
      },
    )

    return () => {
      showing = false
    }
  }, [data, shared, sync, accessToken, auth, queryClient])

  /**
   * Les **pièces jointes** par contrôle : les médias rangés sur la tablette, moins le paraphe.
   * C'est ce compte-là que lisent la colonne « Pièces » et la section des saisies orphelines.
   */
  const attachmentCounts = useMemo(
    () => attachmentCountsOf(attachments.data ?? new Map(), drafts.data ?? []),
    [attachments.data, drafts.data],
  )

  /**
   * « Retirer de la tablette » une saisie hors tournée (#668). Les lectures de tout ce que le
   * retrait a supprimé se relisent ensuite : l'observateur `liveQuery` ne couvre pas la tournée.
   */
  async function discard(inquiryId: string): Promise<DiscardResult> {
    const result = await discardOutOfTour(data, inquiryId)

    if (result === 'removed') {
      for (const queryKey of [
        INQUIRIES_QUERY_KEY,
        DRAFTS_QUERY_KEY,
        MEDIA_QUERY_KEY,
        OUTBOX_QUERY_KEY,
        PASSAGE_NOTICES_QUERY_KEY,
        FIELD_APPOINTMENTS_QUERY_KEY,
      ]) {
        void queryClient.invalidateQueries({ queryKey })
      }
    }

    return result
  }

  const orphans = useMemo(
    () =>
      orphanDraftsOf(drafts.data ?? [], inquiries.data ?? [], outbox.data ?? [], attachmentCounts),
    [drafts.data, inquiries.data, outbox.data, attachmentCounts],
  )

  /**
   * La tournée **telle que le serveur la sert**, et non telle que le cache l'a gardée : ce
   * cache garde une demande réaffectée tant qu'elle porte une saisie (`offline/pull.ts`), elle
   * resterait donc affichée « Planifié » comme si elle était encore de l'équipe.
   *
   * La ligne n'est pas cachée, elle est **reprise par la section au-dessus**, qui dit ce qui
   * lui est arrivé et porte le geste utile. Rien ne disparaît sans contrepartie à l'écran —
   * et un contrôle **transmis** reste ici, à sa place ordinaire : c'est le « transmis » de
   * l'écran, pas un incident.
   */
  const tour = useMemo(() => {
    const moved = new Set(orphans.map((row) => row.draft.inquiryId))

    return (inquiries.data ?? []).filter((inquiry) => !moved.has(inquiry.id))
  }, [orphans, inquiries.data])

  // Mémoïsé sur les quatre lectures Dexie : c'est ce tableau qui alimente la coupure en vues
  // et les options de filtre, dont le tri passe par un `Intl.Collator`.
  //
  // La file est repliée **une fois** en carte : interrogée par demande, elle serait rebalayée
  // autant de fois qu'il y a de lignes, et rien ne l'allège jamais — aucun chemin de la PWA n'en
  // supprime d'entrée, elle porte tous les envois de la vie de la tablette. Les brouillons le
  // sont de même, pour le seul jour du passage (#115).
  //
  // La source est `tour` et non le cache entier : les demandes reprises par la section des
  // saisies orphelines (#147) n'ont pas de ligne ici.
  const rows: InspectionRow[] = useMemo(() => {
    const submissions = latestSubmissions(outbox.data ?? [])
    const performedDays = new Map(
      (drafts.data ?? []).map((entry) => [entry.inquiryId, entry.performedOn ?? null]),
    )

    return tour.map((inquiry) => {
      const submission = submissions.get(inquiry.id)

      return {
        inquiry,
        sync: submission?.state ?? null,
        sentAt: submission?.sentAt ?? null,
        passageNoticedAt: noticedAt.get(inquiry.id) ?? null,
        attachments: attachmentCounts.get(inquiry.id) ?? 0,
        performedOn: performedDays.get(inquiry.id) ?? null,
      }
    })
  }, [tour, outbox.data, attachmentCounts, drafts.data, noticedAt])

  /**
   * La tournée coupée en deux (#109). Rien n'est effacé de la tablette : un contrôle dont le
   * serveur a accusé réception **sort du travail de l'équipe**, il ne disparaît pas — l'agent
   * voit ce qui lui reste à faire, et retrouve ce qui est parti d'un geste. ⚠️ Parti n'est pas
   * accepté : la validation est un geste distinct du bureau, qui peut aussi renvoyer (#181).
   */
  const { todo, transmitted } = useMemo(() => partitionScopes(rows), [rows])
  const scoped = scope === 'todo' ? todo : transmitted

  // Les options se dérivent de la vue entière, jamais du résultat filtré : sinon
  // choisir une ville retirerait toutes les autres du menu, et l'agent serait enfermé dans
  // son propre filtre. La valeur active y entre quoi qu'il arrive — une passe de synchro peut
  // vider la vue de la ville sur laquelle l'agent filtre, et un menu qui ne contient plus ce
  // qu'il affiche n'offre aucun moyen de revenir en arrière.
  const cities = useMemo(
    () =>
      citiesOf(
        scoped.map((row) => row.inquiry),
        filters.city,
      ),
    [scoped, filters.city],
  )
  const states = useMemo(() => statesOf(scoped, filters.state), [scoped, filters.state])
  const purposes = useMemo(
    () =>
      purposesOf(
        scoped.map((row) => row.inquiry),
        filters.purpose,
      ),
    [scoped, filters.purpose],
  )
  const clients = useMemo(
    () =>
      clientsOf(
        scoped.map((row) => row.inquiry),
        filters.clientName,
      ),
    [scoped, filters.clientName],
  )
  // Le secteur actif se cherche dans la tournée entière : c'est son nom que le menu affiche.
  const lots = useMemo(
    () =>
      lotsOf(
        scoped.map((row) => row.inquiry),
        rows.find((row) => row.inquiry.lot?.id === filters.lot)?.inquiry.lot,
      ),
    [scoped, rows, filters.lot],
  )

  const visible = applyFilters(scoped, filters)

  /**
   * L'équipe nomme la tournée, comme le prototype nomme sa campagne — et un agent sans
   * équipe entre quand même, sur une tournée vide (M6) : d'où le repli.
   *
   * Lu par `getSnapshot()` sans s'y abonner : le nom d'équipe est fixé par la connexion et
   * ne change pas sous les pieds de cet écran. Le rail, lui, s'abonne — c'est lui qui suit
   * la montée en ligne.
   */
  const signedIn = auth.getSnapshot()
  const team = signedIn.state === 'signed_out' ? null : signedIn.team?.name

  return (
    <Page>
      <PageHeader
        eyebrow={team === undefined || team === null ? "Tournée de l'équipe" : `Tournée · ${team}`}
        title="Mes contrôles"
      >
        {/*
         * La recherche est dans la barre de titre, et les filtres dans la leur : c'est la
         * séparation du prototype, et elle dit laquelle des deux l'agent utilise le plus.
         * Devant un portail, retrouver une adresse dans quarante lignes est le geste courant —
         * il ne doit pas se payer d'un menu déroulant.
         *
         * Rien à chercher tant que la tournée est vide : le champ dirait à l'agent de fouiller
         * un écran qui porte déjà, en grand, la raison pour laquelle il n'a rien.
         */}
        {rows.length > 0 && (
          <SearchField
            label="Rechercher un contrôle"
            placeholder="Rechercher réf., rue, ville…"
            value={filters.search}
            onValueChange={(search) => setFilters((current) => ({ ...current, search }))}
          />
        )}
        {/* Le contrôle libre (#419) : une maison qu'aucune demande ne nomme. Toujours offert,
            hors réseau compris — la fiche se saisit et part comme les autres. */}
        <Button
          tone="brand"
          onClick={() => {
            setFreeInspection(true)
          }}
        >
          <Plus size={18} aria-hidden />
          Contrôle libre
        </Button>
      </PageHeader>

      {/*
       * Les deux barres sont **épinglées** : le prototype pose sa `.ct-toolbar` à côté de la
       * zone défilante, jamais dedans. Une tournée fait quarante lignes, et une barre partie
       * avec le défilement ne se rattrape qu'en remontant toute la liste.
       */}
      {rows.length > 0 && (
        <PageToolbar className={s.toolbar}>
          {/*
           * Le sélecteur de vues (#109). Toujours affiché dès qu'il y a une tournée, même
           * avec zéro transmis : c'est lui qui dit à l'agent **où sont passés** les contrôles
           * qui quittent sa liste, faute de quoi la correction ressemblerait à une perte de
           * données.
           *
           * Au-dessus de la barre de filtres, et non dedans : il choisit **quelle liste** on
           * regarde, là où la barre restreint celle qui est déjà choisie.
           */}
          <SegmentedControl
            label="Vues de la tournée"
            value={scope}
            onValueChange={(next) => {
              void navigate({ search: { view: next } })
              // Les deux vues sont deux listes : une ville, un état — ou une recherche —
              // retenu de l'autre viderait celle-ci sans que rien à l'écran n'en donne la
              // raison.
              //
              // Le **tri**, lui, est conservé : c'est un ordre total, il ne peut vider aucune
              // vue. L'effacer ferait perdre à l'agent qui trie par « Réf. GHP » pour recouper
              // une liste papier son repère, à chaque aller-retour.
              setFilters((current) => ({ ...NO_FILTERS, sort: current.sort }))
            }}
            options={[
              { value: 'todo', label: 'À faire', count: todo.length },
              { value: 'transmitted', label: 'Transmis', count: transmitted.length },
            ]}
          />

          {scoped.length > 0 && (
            <FilterBar
              actions={
                <>
                  {/*
                   * Le compteur du prototype. « n sur m » dès qu'un filtre est posé : sans
                   * le total, rien ne dit à l'agent qu'il ne voit qu'une partie de sa
                   * tournée — et une tournée qui semble courte se termine trop tôt.
                   */}
                  <span className={s.count}>
                    {visible.length === scoped.length
                      ? `${scoped.length} ${scoped.length > 1 ? 'contrôles' : 'contrôle'}`
                      : `${visible.length} sur ${scoped.length}`}
                  </span>
                  {hasActiveFilters(filters) && (
                    <Button tone="neutral" size="sm" onClick={() => setFilters(NO_FILTERS)}>
                      <RotateCcw size={16} aria-hidden />
                      Réinitialiser
                    </Button>
                  )}
                </>
              }
            >
              {/*
               * Allumés **quand ils restreignent**, comme les `.fbtn.active` du prototype — qui
               * compare la valeur à « tous » et laisse le tri éteint quoi qu'il arrive : un tri
               * est un ordre, il ne retire aucune ligne, et l'allumer noierait le repère.
               */}
              <Select
                width="auto"
                active={filters.state !== null}
                aria-label="Filtrer par état"
                placeholder="Tous les états"
                value={filters.state}
                onValueChange={(state) =>
                  setFilters((current) => ({
                    ...current,
                    state: state as TourState | null,
                  }))
                }
                options={states.map((state) => ({
                  value: state,
                  label: TOUR_STATE_PRESENTATION[state].label,
                }))}
              />
              <Select
                width="auto"
                active={filters.city !== null}
                aria-label="Filtrer par ville"
                placeholder="Toutes les villes"
                value={filters.city}
                onValueChange={(city) => setFilters((current) => ({ ...current, city }))}
                options={cities.map((city) => ({ value: city, label: city }))}
              />
              {/* ⚠️ **Rendus seulement quand la vue en porte**, contrairement à la ville et à
                  l'état : une tournée d'un seul type ou d'un seul client n'a rien à filtrer, et
                  un menu à une entrée occupe une barre déjà large sur 1024 px. */}
              {purposes.length > 1 && (
                <Select
                  width="auto"
                  active={filters.purpose !== null}
                  aria-label="Filtrer par type de contrôle"
                  placeholder="Tous les types"
                  value={filters.purpose}
                  onValueChange={(purpose) => setFilters((current) => ({ ...current, purpose }))}
                  options={purposes.map((purpose) => ({
                    value: purpose,
                    label: inquiryPurposeLabel(purpose) ?? purpose,
                  }))}
                />
              )}
              {clients.length > 1 && (
                <Select
                  width="auto"
                  active={filters.clientName !== null}
                  aria-label="Filtrer par client"
                  placeholder="Tous les clients"
                  value={filters.clientName}
                  onValueChange={(clientName) =>
                    setFilters((current) => ({ ...current, clientName }))
                  }
                  options={clients.map((name) => ({ value: name, label: name }))}
                />
              )}
              {/* Libre (#580) : il n'attend pas qu'on choisisse un client, contrairement au
                  bureau — une tournée ne mêle que quelques clients, et le libellé nomme le
                  secteur. */}
              {lots.length > 1 && (
                <Select
                  width="auto"
                  active={filters.lot !== null}
                  aria-label="Filtrer par secteur"
                  placeholder="Secteur : tous"
                  value={filters.lot}
                  onValueChange={(lot) => setFilters((current) => ({ ...current, lot }))}
                  options={lots.map((lot) => ({ value: lot.id, label: lotLabel(lot) }))}
                />
              )}
              <Select
                width="auto"
                aria-label="Trier la tournée"
                value={filters.sort}
                onValueChange={(sort) =>
                  setFilters((current) => ({ ...current, sort: sort ?? NO_FILTERS.sort }))
                }
                options={SORT_OPTIONS}
              />
            </FilterBar>
          )}
        </PageToolbar>
      )}

      <PageBody>
        {/* Avant la tournée, et non après : un relevé que la liste ne porte plus n'existe
            nulle part ailleurs, et il ne doit pas se lire sous le pli. */}
        <OrphanDrafts rows={orphans} onDiscard={discard} />

        {rows.length === 0 ? (
          <EmptyState
            icon={<ClipboardList size={18} aria-hidden />}
            title={EMPTY_TOUR[download].title}
          >
            {EMPTY_TOUR[download].body}
          </EmptyState>
        ) : (
          <>
            {/*
             * La note ne parle que de lignes présentes. Sur une tournée neuve, elle
             * s'afficherait au-dessus de « Aucun contrôle transmis » et affirmerait des faits
             * sur un ensemble vide.
             */}
            {scope === 'transmitted' && scoped.length > 0 && (
              <InfoNote className={s.scopeNote} icon={<Send size={15} />}>
                Ces contrôles ont été transmis et attendent la validation du bureau : ils ne sont
                plus à faire et ne peuvent plus être modifiés. Le bureau peut encore en renvoyer un
                pour correction, avec un motif — il reviendrait alors dans « À faire ». La fiche et
                ses pièces restent consultables ici jusqu'à ce que le bureau tranche : elles
                quittent alors la tablette, le bureau les ayant en entier.
              </InfoNote>
            )}

            {scoped.length === 0 ? (
              <EmptyState
                icon={<CheckCheck size={18} aria-hidden />}
                title={EMPTY_SCOPE[scope].title}
              >
                {EMPTY_SCOPE[scope].body}
              </EmptyState>
            ) : (
              <Card>
                {/*
                 * ⚠️ « Type » est revenue (#674, Terrain 0.5d) en payant sa place : la rue, le code
                 * postal et la commune tiennent dans une seule colonne « Adresse », sur deux lignes
                 * comme dans « Historique ». Trois colonnes plus « Type » faisaient sortir
                 * « Pièces » sur une tournée ordinaire à 1 280 px. « N° » reste dehors : le numéro
                 * de voie fait partie de la rue (#81).
                 */}
                <DataTable>
                  <thead>
                    <tr>
                      <th scope="col" className={s.caseReference}>
                        Dossier
                      </th>
                      <th scope="col" className={s.reference}>
                        Réf. GHP
                      </th>
                      <th scope="col">Adresse</th>
                      <th scope="col" className={s.purpose}>
                        Type
                      </th>
                      <th scope="col" className={s.appointment}>
                        RDV
                      </th>
                      <th scope="col" className={s.status}>
                        État
                      </th>
                      <th scope="col" className={s.status}>
                        Synchro
                      </th>
                      <th scope="col" className={s.attachments} data-align="end">
                        Pièces
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((row) => (
                      <InspectionLine key={row.inquiry.id} row={row} onOpen={setOpening} />
                    ))}
                  </tbody>
                </DataTable>
                {visible.length === 0 && (
                  <p className={s.noMatch} role="status">
                    Aucun contrôle ne correspond à ces filtres.
                  </p>
                )}
              </Card>
            )}
          </>
        )}

        {/*
         * La pré-fiche du prototype : l'adresse, le contact et le rendez-vous **avant** la
         * saisie. C'est ce que l'agent lit depuis le trottoir, et la seule chose qui lui évite
         * d'ouvrir une fiche pour vérifier qu'il est au bon portail. L'agenda ouvre la même.
         */}
        <OpeningSheet
          inquiry={opening?.inquiry ?? null}
          state={opening === null ? undefined : tourStateOf(opening)}
          queued={
            opening !== null && hasQueuedHandOver(opening.sync, locked.has(opening.inquiry.id))
          }
          onClose={() => {
            setOpening(null)
          }}
          // Un contrôle libre n'a ni rendez-vous ni usager à prévenir : la pré-fiche n'en offre
          // que l'ouverture.
          {...(opening?.inquiry.freeInspection === undefined
            ? {
                onPassageNotice: recordNotice,
                onAppointment: recordAppointmentOnSite,
                move: appointmentMove,
              }
            : {})}
          tour={occupancy}
          onOpen={(inquiry) => {
            setOpening(null)
            // La vue voyage jusqu'à l'éditeur : son « Retour » ramène l'agent sur la liste
            // qu'il a quittée, avec la ligne d'où il vient encore à l'écran.
            void navigate({
              to: '/inspections/$inquiryId',
              params: { inquiryId: inquiry.id },
              search: { view: scope },
            })
          }}
        />
        <FreeInspectionDialog
          open={freeInspection}
          onOpenChange={setFreeInspection}
          onStart={(input) => {
            void createFreeInspection(data, input, () => new Date()).then((inquiryId) =>
              navigate({
                to: '/inspections/$inquiryId',
                params: { inquiryId },
                search: { view: scope },
              }),
            )
          }}
        />
      </PageBody>
    </Page>
  )
}

/** Ce qu'affiche la colonne « Réf. GHP » d'un contrôle qui n'en a pas : le bureau la donne. */
function referenceless(inquiry: CachedInquiry): string {
  return inquiry.freeInspection === undefined ? 'Sans référence' : 'Contrôle libre'
}

/** Ce qu'affiche une cellule dont la donnée manque **sur ce contrôle-là**. */
const MISSING = '—'

/**
 * Toucher une ligne ouvre la **pré-fiche**, pas l'éditeur — le chemin du prototype. L'agent
 * arrive devant une maison : ce qu'il vérifie d'abord est l'adresse et le nom à appeler, et
 * l'éditeur le lui ferait lire par-dessus une saisie qu'il n'a pas demandé à commencer.
 *
 * ⚠️ **La cible est la rangée entière**, comme au prototype (`<tr data-open>`), et non la seule
 * étiquette de la référence : c'est `DataTableRowAction` qui l'étale, et rien ici ne redit ce
 * que la peau du tableau porte déjà.
 */
function InspectionLine({
  row,
  onOpen,
}: {
  row: InspectionRow
  onOpen: (row: InspectionRow) => void
}) {
  const { inquiry } = row
  // L'état **dans la tournée**, pas le statut en cache : l'API cesse de servir une demande une
  // fois déposée, donc son `status` reste celui d'avant le dépôt (#181).
  const state = TOUR_STATE_PRESENTATION[tourStateOf(row)]
  const sync = row.sync === null ? null : SYNC_PRESENTATION[row.sync]
  const town = townLine(inquiry)

  return (
    // La case « Urgent » du bureau (BACK-OFFICE-X), quel que soit l'état. `=== true` : un cache
    // d'avant le champ le relit `undefined`.
    <tr data-urgent={inquiry.urgent === true ? '' : undefined}>
      <td className={s.mono}>
        <UrgentStack urgent={inquiry.urgent}>{inquiry.caseReference ?? MISSING}</UrgentStack>
      </td>
      <td className={s.mono}>
        {/* ⚠️ Le **verbe** dans le nom accessible : au clavier comme au lecteur d'écran, une
            cible nommée « GHP-2026-0142 » ne dit pas ce qu'elle fait. Le nom contient la
            référence visible, qui reste donc prononçable telle quelle (WCAG 2.5.3). */}
        <DataTableRowAction
          aria-label={`Ouvrir le contrôle ${inquiry.reference ?? referenceless(inquiry).toLowerCase()}`}
          onClick={() => {
            onOpen(row)
          }}
        >
          {inquiry.reference ?? referenceless(inquiry)}
          {/* Le chevron du prototype, porté par le bouton : décoratif, il n'est pas une
              seconde cible et n'ajoute rien au nom accessible. */}
          <ChevronRight size={16} aria-hidden className={s.chevron} />
        </DataTableRowAction>
      </td>
      <td>
        <span className={s.street}>{inquiry.address.street ?? 'Adresse inconnue'}</span>
        {town !== '' && <span className={s.town}>{town}</span>}
      </td>
      <td>
        <Purpose inquiry={inquiry} />
      </td>
      <td>
        <span className={s.appointmentCell}>
          <Appointment scheduledAt={inquiry.scheduledAt ?? null} performedOn={row.performedOn} />
        </span>
      </td>
      <td>
        <Badge tone={state.tone}>{state.label}</Badge>
      </td>
      <td>
        {sync === null ? (
          <span className={s.absent}>{MISSING}</span>
        ) : (
          <Badge tone={sync.tone}>{sync.label}</Badge>
        )}
      </td>
      <td data-align="end" className={s.mono}>
        <span className={s.attachmentCount}>
          <Paperclip size={18} aria-hidden />
          {row.attachments}
        </span>
      </td>
    </tr>
  )
}

/** La seconde ligne de l'adresse : « 44470 Thouaré-sur-Loire », ou ce qu'il en reste. */
function townLine(inquiry: CachedInquiry): string {
  return [inquiry.address.postalCode, townOf(inquiry.address)]
    .filter((part) => part !== null)
    .join(' ')
}

/**
 * Le type, avec le libellé de l'agenda pour une contre-visite (« Vente Contre-visite »). Un type
 * inconnu s'affiche tel quel (`inquiryPurposeLabel`) ; un type absent, le tiret, et la ligne
 * reste (M21).
 */
function Purpose({ inquiry }: { inquiry: CachedInquiry }) {
  const label = counterVisitLabel(inquiry.purpose ?? null, inquiry.followUpRank ?? null)

  return label === null ? <span className={s.absent}>{MISSING}</span> : <Tag>{label}</Tag>
}

/**
 * La colonne « RDV » : le rendez-vous pris, et **l'écart quand le passage a eu lieu un autre
 * jour** (#115). C'est le seul endroit de la tournée où l'agent voit d'un coup d'œil quels
 * contrôles ont glissé.
 *
 * ⚠️ On ne réaligne rien et on ne cache rien : la pastille ambre annonce le jour réellement
 * saisi à côté du jour prévu (M21). Un contrôle fait au rendez-vous n'affiche que la date —
 * une pastille sur chaque ligne ne dirait plus rien.
 */
function Appointment({
  scheduledAt,
  performedOn,
}: {
  scheduledAt: string | null
  performedOn: string | null
}) {
  // ⚠️ Le jour se lit par la garde du domaine, jamais par un test de `null` : une valeur que
  // `performedOnSchema` refuse partait autrefois à l'affichage, et la colonne affirmait « Fait le
  // 6 août » pendant que l'écart, la carte et la charge utile transmise la tenaient pour absente.
  const performed = calendarDay(performedOn)

  // Sans rendez-vous — le cas courant tant que le bureau n'en pose pas — la colonne dit quand
  // même le jour déclaré : « — » effaçait la seule date que l'agent ait saisie, sur la classe
  // de lignes la plus nombreuse. Pas d'écart à annoncer ici, il n'y a rien à comparer.
  if (scheduledAt === null) {
    return performed === null ? (
      <span className={s.absent}>{MISSING}</span>
    ) : (
      <Badge tone="info">
        <CalendarClock size={14} aria-hidden /> Fait le {formatShort(performed)}
      </Badge>
    )
  }

  const deviation = appointmentDeviation(scheduledAt, performedOn)

  return (
    <>
      {/* Le jour seul : la tournée n'a pas la place d'une heure. */}
      <span className={s.mono}>{appointmentSlot(scheduledAt)?.shortDay ?? MISSING}</span>
      {deviation.kind === 'shifted' && performed !== null && (
        <Badge tone="warn">
          <CalendarClock size={14} aria-hidden /> Fait le {formatShort(performed)}
        </Badge>
      )}
    </>
  )
}

function formatShort(date: Date): string {
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
}

/**
 * Compte les médias par contrôle, depuis la liste des contrôles auxquels ils se rattachent.
 *
 * ⚠️ Des `inquiryId`, **jamais des clés d'index** : l'index des médias est composé
 * (`[teamId+inquiryId]`), donc une clé est un tableau et un `String(clé)` rendrait
 * « équipe,contrôle ». La map ne s'apparierait plus à aucune demande et **tous** les compteurs
 * afficheraient zéro, sans erreur. L'accesseur cloisonné rend déjà des identifiants
 * (`media.inquiryIds()`) : il n'y a plus de clé à interpréter ici.
 */
function countByInquiry(inquiryIds: readonly string[]): Map<string, number> {
  const counts = new Map<string, number>()

  for (const inquiryId of inquiryIds) {
    counts.set(inquiryId, (counts.get(inquiryId) ?? 0) + 1)
  }

  return counts
}

/**
 * Les **pièces jointes** par contrôle : les médias, moins la signature.
 *
 * ⚠️ La signature est un média, pas une pièce jointe — c'est déjà la règle de l'éditeur, dont
 * l'onglet « Pièces jointes » écarte le type `signature`. Les deux compteurs se contredisaient
 * sur le même contrôle : 1 dans l'onglet, 2 dans la colonne (#181). Une seule règle, appliquée
 * aux deux endroits.
 *
 * ⚠️ Le paraphe se déduit du **brouillon** (`signatureMediaId`) et non du type du média : le
 * compte des médias se lit sur les **clés** de l'index, qui ne portent pas le type. Ouvrir les
 * enregistrements pour l'y lire ferait passer toutes les photos et vidéos en attente d'envoi par
 * `structuredClone` à chaque écriture média — c'est précisément ce que `media.inquiryIds()`
 * existe pour éviter.
 *
 * ⚠️ Jamais en dessous de zéro : un brouillon de l'arriéré est lisible de toutes les équipes
 * quand son paraphe peut être rangé sous une autre, et le compte ne le porte alors pas.
 */
function attachmentCountsOf(
  media: ReadonlyMap<string, number>,
  drafts: readonly InspectionDraft[],
): Map<string, number> {
  const counts = new Map(media)

  for (const draft of drafts) {
    if (draft.signatureMediaId === null) continue

    const counted = counts.get(draft.inquiryId)
    if (counted !== undefined) counts.set(draft.inquiryId, Math.max(0, counted - 1))
  }

  return counts
}
